import { OrganizationStatus } from "@helpers/organizationHelper";
import { translate } from "react-i18nify";
import { WordResponseType } from "./assessmentSlice";
import { SelectOption } from "@components/LSelect";

export interface Entity<T> {
    id: T;
}

export enum Languages {
    English = 1,
    Spanish = 2
}

export enum AssessmentStatus {
    NotStarted = "Not Started",
    Started = "Started",
    Complete = "Complete",
    Incomplete = "Incomplete",
    Invalidated = "Invalidated",
    ReviewNeeded = "Review Needed",
    ReviewComplete = "Review Complete",
    Deleted = "Deleted",
    Archived = "Archived",
    LinkExpired = "Link Expired",
    ThreeFailedLoginAttempts = "Three Failed Login Attempts",
    EmaillinkActivated = "Email Link Activated"
}

export enum AssessmentResultsCode {
    Normal = "Normal",
    Borderline = "Borderline",
    Impaired = "Impaired",
    Uninterpretable = "Uninterpretable",
    Discontinued = "Discontinued"
}

export interface UserLocation extends Entity<number> {
    name: string;
    roles?: Role[];
    acceptedRoles?: Role[];
}

export interface InviteUser {
    firstName: string;
    lastName: string;
    email: string;
    title?: string;
    locations?: UserLocation[];
}

export interface InviteUserArray {
    users: Array<Omit<InviteUser, "roles" | "locations">>;
}

export interface ExistingUser extends Entity<string>, InviteUser {
    status: OrganizationUserStatus;
    lastActivity?: string;
}

export enum OrganizationUserStatus {
    InviteSent = "Invite sent",
    Active = "Active",
    Inactive = "Inactive",
    Deleted = "Deleted"
}

export enum OrganizationUserStatusId {
    InviteSent = 1,
    Active = 2,
    Inactive = 3,
    Deleted = 4
}

export enum UserStatusId {
    Active = 1,
    Inactive = 2
}

export enum UserStatus {
    Active = "active",
    Inactive = "inactive",
    AlreadyExist = "alreadyExist"
}

export type getOrganizationUsersSortOptions = "email" | "firstName" | "lastName" | "role" | "location" | "status";

export type getOrganizationsSortOptions = "name" | "id" | "assessments" | "status";

export interface Role {
    id: RoleId;
    name: string;
    description: string;
    permissions: Permission[];
}

export enum RoleId {
    Admin = 1,
    Facilitator = 2,
    Clinician = 3,
    Researcher = 4,
    DataQA = 5,
    ClinicalStaff = 6,
    SuperAdmin = 7
}

export const prettyPrintRoleId = (roleId: RoleId): string => {
    switch (roleId) {
        case RoleId.Admin:
            return translate("admin.admin");
        case RoleId.Facilitator:
            return translate("admin.facilitator");
        case RoleId.Clinician:
            return translate("admin.clinician");
        case RoleId.ClinicalStaff:
            return translate("admin.clinicalStaff");
        case RoleId.Researcher:
            return translate("admin.researcher");
        case RoleId.DataQA:
            return translate("admin.dataQA");
        default:
            return "";
    }
};

export const prettyPrintRoleIds = (roleIds: RoleId[]): string => {
    const result: string[] = [];

    if (roleIds) {
        roleIds.forEach((role) => {
            result.push(prettyPrintRoleId(role));
        });
    }

    return result.join(", ");
};

export enum LanguageId {
    en = 1,
    es = 2
}

export enum Sex {
    Female = "female",
    Male = "male",
    Other = "other"
}

export const RaceOptions = [
    { name: "American Indian or Alaska Native", value: "1" } as SelectOption,
    { name: "Asian", value: "2" } as SelectOption,
    { name: "Black or African American", value: "3" } as SelectOption,
    { name: "Hispanic or Latino", value: "4" } as SelectOption,
    { name: "Native Hawaiian or Other Pacific Islander", value: "5" } as SelectOption,
    { name: "White", value: "6" } as SelectOption
];

export const EthnicityOptions = [
    { name: "Hispanic or Latino", value: "1" } as SelectOption,
    { name: "Not Hispanic or Latino", value: "2" } as SelectOption
];

export enum Race {
    americanIndianAlaskaNative = 1,
    asian = 2,
    blackAfricanAmerican = 3,
    hispanicLatino = 4,
    nativeHawaiianOtherPacificIslander = 5,
    white = 6
}

export enum Ethnicity {
    hispanicLatino = 1,
    notHispanicLatino = 2
}

export interface ExistingExamineeDetails extends Entity<number> {
    clinicianId: string;
    clinicianFirstName: string;
    clinicianLastName: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    birthDate: string;
    sex: Sex;
    race?: Race;
    ethnicity?: Ethnicity;
    highestLevelEducation?: string;
    primaryLanguage: string;
    secondaryLanguages?: string[];
}

export interface BulkResponseApi {
    stepName: string;
    responses: ResponseApi[];
}

export interface ResponseApi {
    response: string;
    responseType: WordResponseType;
}

export interface Note {
    id?: number;
    text: string;
    createdDate: string;
    firstName: string;
    lastName: string;
    roles: RoleId[];
}

export interface WordResponseApi {
    id?: number;
    stepName: string;
    targetWord: string;
    response?: string;
    responseType: WordResponseType;
    tryNumber: number;
    audio?: Blob;
    audioRecordingUri?: string;
    notes?: Note[];
    source?: SttSourceType;
}

export enum SttSourceType {
    Google = "google",
    Azure = "azure"
}

export interface Paginated<T> {
    pageNumber: number;
    totalPages: number;
    totalCount: number;
    hasPreviousPage: boolean;
    hasNextPage: boolean;
    items: T[];
}

export enum SortDirection {
    Ascending = "asc",
    Descending = "desc"
}

export interface OrganizationUsers extends Paginated<ExistingUser> {}

export interface ExistingAssessment extends Entity<number> {
    examineeId: string;
    examineeFirstName: string;
    examineeLastName: string;
    visitNumber: number;
    formId: number;
    formName: string;
    date: string;
    status: AssessmentStatus;
    examineeEmail?: string;
    examineePhoneNumber?: string;
    exceptions: number;
    score: AssessmentResultsCode;
}

/**
 * This is an interface that directly matches the api call:
 * /locations/{locationId}/assessments/{assessmentId}/results
 */
export interface AssessmentResult extends Entity<number> {
    assessmentFormId: number;
    assessmentFormName: string;
    clinicianFirstName: string;
    clinicianId: string;
    clinicianLastName: string;
    displayId: string;
    examineeId: number;
    facilitatorFirstName: string;
    facilitatorId: string;
    facilitatorLastName: string;
    languageId: number;
    languageName: string;
    launchedDate: string;
    isRemote?: boolean;
    assessmentCount?: number;
    locationId: number;
    locationName: string;
    result: ScoreResult;
    responses: ResponseResult[];
    assessmentId: number;
    status: AssessmentStatus;
    visitNumber: number;
    notes?: Note[];
}

export interface ResultSection {
    totalWords: number;
    totalCorrect: number;
    totalIncorrect: number;
    title: string;
    isRecall: boolean;
    isCued: boolean;
    result: ResponseResult[];
}

export interface ScoreResult {
    pieRatio: Score;
    pieRatioB2?: Score | null;
    totalLearningScore: Score;
    trialScores: Score[];
}

interface Score {
    name: string;
    score: number;
    normalThreshold?: 0;
    status: AssessmentResultsCode;
    normalRange?: string;
    marginalRange?: string;
    impairedRange?: string;
    uninterpretableRange?: string;
}

export interface ResponseResult {
    trial: string;
    audioRecordingUri: string;
    responses: WordResponseApi[];
}

export interface ExistingAssessments extends Paginated<ExistingAssessment> {}

export interface AssessmentHistory extends Entity<number> {
    hasExecution?: boolean;
    displayId: string;
    assessmentFormId: number;
    assessmentFormName: string;
    visitNumber: number;
    status: AssessmentStatus;
    launchedDate: string;
    assessmentType: string;
}

export interface AvailableAssessment {
    purchases: number;
    availableAssessments: number;
    availableAssessmentPercentage: number;
    usedAssessments: number;
    warning: boolean;
    threshold: number;
}

export interface AssessmentScores {
    totalLearning?: AssessmentScore;
    b1CuedRecall?: AssessmentScore;
    b1Intrusions?: AssessmentScore;
    b2CuedRecall?: AssessmentScore;
    b2CuedIntrusions?: AssessmentScore;
    pieRatio?: AssessmentScore;
    pieRatioB2?: AssessmentScore;
}

export interface AssessmentScore {
    score: number;
    result: AssessmentResultsCode;
}

export interface AssessmentNote extends Entity<number> {
    description: string;
    date: Date;
}

export enum ScoringCategories {
    TotalLearning = "Total Learning",
    CuedRecallB1 = "Cued Recall B1",
    B1Intrusions = "Intrusions B1",
    CuedRecallB2 = "Cued Recall B2",
    CuedB2Intrusions = "Intrusions B2",
    CuedB1Intrusions = "Cued B1 Intrusions; PIE Ratio"
}
export interface UserDetail extends Entity<string> {
    firstName: string;
    lastName: string;
    email: string;
    title?: string;
    jobTitle: string;
    isAdmin: boolean;
    hasAccess: boolean;
    organizations: OrganizationPermission[];
    locations: LocationPermission[];
}

export interface UserInfo extends Entity<string> {
    title: string;
    firstName: string;
    lastName: string;
    status: UserStatus;
    hasOrganization: boolean;
}

export interface UserUpdate {
    title?: string;
    firstName: string;
    lastName: string;
    password: string;
}

export enum PermissionCategory {
    administrative = "administrative",
    assessmentBased = "assessmentBased",
    resultBased = "resultBased"
}

export interface Permission extends Entity<number> {
    name: string;
    roles: number[];
    category: PermissionCategory;
}

export interface OrganizationPermission extends Permission {
    status: OrganizationStatus;
}

export interface LocationPermission extends Permission {
    organizationId: number;
    delegateUsers: DelegateUser[];
}

export interface DelegateUser {
    userId: string;
    firstName: string;
    lastName: string;
    roles: number[];
}

export interface PricingTier {
    name: string;
    minimumQuantity: number;
    maximumQuantity: number;
    unitPrice: number;
    discount: number;
}

export interface CreatePurchaseApiResponse {
    checkoutSessionId: string;
}

export interface Purchase {
    id: number;
    quantity: number;
    unitPrice: number;
    totalAmount: number;
    discount?: number;
    status: PurchaseStatus;
    purchaseDate: string;
    assessmentsUsed: number;
    checkoutSessionId: string;
}

export enum PurchaseStatus {
    Initiated = "Initiated",
    Succeeded = "Succeeded",
    Failed = "Failed"
}

export type AssessmentDeletionMode = "delete" | "archive";

export enum InternetStatus {
    Online = 1,
    Timeout = 2,
    Disconected = 3
}

/**
 * This is an interface that directly matches the api call:
 * /organizations/
 */
export interface Organization {
    name: string;
    id: number;
    status: OrganizationStatus;
    contactFirstName: string;
    contactLastName: string;
    totalAssessments: number;
    usedAssessments: number;
}

export interface Organizations extends Paginated<Organization> {}

export enum DocumentationCategory {
    Video = "video",
    Document = "document",
    Faq = "faq"
}

export interface Documentation extends Entity<number> {
    title: string;
    description: string;
    fileUrl: string;
    category: DocumentationCategory;
}

export interface WordRequest {
    targetWord: string;
    response: string;
    count: number;
}

export interface WordRequests extends Paginated<WordRequest> {}

export interface LoginRedirect {
    pathname: string;
}

export interface Agreement {
    id: number;
    agreementType: AgreementType;
    documentUrl: string;
    active: boolean;
}

export enum AgreementType {
    ProviderAgreement = "providerAgreement",
    EndUserLicenseAgreement = "endUserLicenseAgreement"
}
